/**
 * i18n configuration — the single source of truth for which locales the SPA
 * supports, which of them are written right-to-left, and how the choice is
 * persisted.
 *
 * The app copy is authored in FRENCH, so `fr` is both the default and the
 * fallback locale: any key not yet externalised (still a hardcoded French
 * string in a component) or missing from a translated catalog resolves to the
 * French source. `en` is a full translation of the externalised tranche; `ar`
 * is scaffolded as the right-to-left example (drives `document.dir = 'rtl'`).
 */

/** Every locale the UI can switch to. Order is the order shown in pickers. */
export const SUPPORTED_LOCALES = ['fr', 'en', 'ar'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** French is the source language → both the default and the fallback. */
export const DEFAULT_LOCALE: Locale = 'fr';
export const FALLBACK_LOCALE: Locale = 'fr';

/**
 * Locales written right-to-left. On a switch to any of these, the locale store
 * sets `document.documentElement.dir = 'rtl'` (otherwise `'ltr'`). Kept as a Set
 * so adding a future RTL locale (he, fa, ur…) is a one-line change.
 */
export const RTL_LANGUAGES: ReadonlySet<Locale> = new Set<Locale>(['ar']);

/** localStorage key holding the raw locale string (e.g. "en"). */
export const LOCALE_STORAGE_KEY = 'psn-locale';

/** Display metadata for each locale — used by the Profile language selector. */
export const LOCALE_META: Record<Locale, { label: string; nativeName: string; dir: 'ltr' | 'rtl' }> =
  {
    fr: { label: 'Français', nativeName: 'Français', dir: 'ltr' },
    en: { label: 'English', nativeName: 'English', dir: 'ltr' },
    ar: { label: 'العربية', nativeName: 'العربية', dir: 'rtl' },
  };

/** Type-guard: coerce any persisted/legacy value into a known locale. */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Reading direction for a locale. */
export function dirFor(locale: Locale): 'ltr' | 'rtl' {
  return RTL_LANGUAGES.has(locale) ? 'rtl' : 'ltr';
}
